import { useEffect, useRef, useState } from "react";
import { api, puede, type Paciente } from "../api.ts";
import { FINES_DATOS, textoAutorizacionDatos } from "../../../dominio/src/index.ts";
import { AvisoError, useAccion } from "../pantallas/comun.tsx";
import { FirmaPad } from "./FirmaPad.tsx";
import { AutorizacionDatosParaFirmar, useHojaParaImprimir } from "./FormularioFirma.tsx";
import { useClinica } from "../clinica.ts";

interface EstadoDatos { actual: { estado: string } | null; firmante: string; calidad: string; menor: boolean }

const omitida = (id: string) => `donna.aut-datos.${id}`;
const yaOmitida = (id: string) => { try { return sessionStorage.getItem(omitida(id)) === "1"; } catch { return false; } };

/**
 * Al abrir la ficha de un paciente que aún no autorizó el uso de sus datos personales (LOPDP), pide la autorización:
 * se lee en pantalla y se firma ahí mismo, o se imprime para firmarla a mano y luego se registra como firmada en papel.
 * «Ahora no» la deja para después (no vuelve a salir en esta sesión; la autorización sigue disponible en Ficha › Datos personales).
 */
export function AutorizacionDatosAlAbrir({ paciente }: { paciente: Paciente }) {
  const [estado, setEstado] = useState<EstadoDatos | null>(null);
  const [abierta, setAbierta] = useState(false);
  const [fines, setFines] = useState<Record<string, boolean>>({ atencion: true });
  const [firma, setFirma] = useState<string | null>(null);
  const [impresa, setImpresa] = useState(false);
  const [hoja, imprimir] = useHojaParaImprimir();
  const clinica = useClinica();
  const a = useAccion();
  const cerrar = useRef<HTMLButtonElement>(null);
  const url = `/api/pacientes/${paciente.id}/datos-personales`;
  const escribe = puede("pacientes:escribir");

  useEffect(() => {
    setAbierta(false); setEstado(null); setFirma(null); setImpresa(false); setFines({ atencion: true });
    if (!escribe || yaOmitida(paciente.id)) return;
    let vivo = true;
    api<EstadoDatos>("GET", url).then((e) => { if (vivo && !e.actual) { setEstado(e); setAbierta(true); } }).catch(() => undefined);
    return () => { vivo = false; };
  }, [paciente.id]);
  useEffect(() => { if (abierta) cerrar.current?.focus(); }, [abierta]);

  if (!abierta || !estado) return <>{hoja}</>;
  const menor = estado.menor;
  const t = textoAutorizacionDatos({ nombre: clinica?.nombre ?? "La clínica", razonSocial: clinica?.razonSocial, ruc: clinica?.ruc, correo: clinica?.correo, telefono: clinica?.telefono }, menor);
  const sinRepresentante = menor && !estado.firmante;
  const quien = menor ? "representante legal" : "titular";
  const posponer = () => { try { sessionStorage.setItem(omitida(paciente.id), "1"); } catch { /* sin almacenamiento: vuelve a preguntar */ } setAbierta(false); };
  async function registrar(papel: boolean) {
    const r = await a.correr(() => api<unknown>("POST", `${url}/consentimiento`, { fines, ...(papel ? { papel: true } : { firma }) }));
    if (r !== undefined) setAbierta(false);
  }

  return (
    <>
      {hoja}
      <div className="modal-fondo" role="dialog" aria-modal="true" aria-labelledby="t-aut-datos" onKeyDown={(e) => { if (e.key === "Escape") posponer(); }}>
        <section className="card aut-datos">
          <div className="fila" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
            <h2 id="t-aut-datos" style={{ margin: 0 }}>Autorización para el uso de datos personales</h2>
            <button ref={cerrar} className="btn chico" onClick={posponer} title="No vuelve a salir en esta sesión; se puede registrar después en Ficha, Datos personales">Ahora no</button>
          </div>
          <p className="muted" style={{ marginTop: 6 }}>
            <b>{paciente.nombres} {paciente.apellidos}</b> no ha autorizado todavía el uso de sus datos. Léela con {menor ? "el representante legal" : "el paciente"} y regístrala firmada en pantalla o impresa.
          </p>
          <h3 style={{ margin: "10px 0 4px" }}>{t.titulo}</h3>
          {t.parrafos.map((x, i) => <p key={i} style={{ margin: "4px 0" }}>{x}</p>)}
          <div className="eyebrow">Mis decisiones</div>
          <div className="ft-aut">
            {t.fines.map((f) => (
              <label key={f.clave}>
                <input type="checkbox" checked={f.obligatorio || !!fines[f.clave]} disabled={f.obligatorio || a.ocupado} onChange={(x) => setFines({ ...fines, [f.clave]: x.target.checked })} />{" "}
                <b>{f.texto}</b>{f.obligatorio && <small className="muted"> · necesario para la atención</small>}<br /><small className="muted">{f.detalle}</small>
              </label>
            ))}
          </div>
          <details>
            <summary>Mis derechos</summary>
            {t.derechos.map((x, i) => <p key={i}>{x}</p>)}
          </details>
          {t.cierre.map((x, i) => <p key={i} style={{ margin: "4px 0" }}>{x}</p>)}
          {sinRepresentante ? (
            <div className="aviso">Para firmar en pantalla, registra primero al representante legal en la ficha del paciente. Mientras, puedes imprimir la hoja.</div>
          ) : (
            <div className="firma-lopdp">
              <span className="muted" style={{ fontSize: 13 }}>Firma del {quien}: <b>{estado.firmante}</b></span>
              <FirmaPad alCambiar={setFirma} />
            </div>
          )}
          <AvisoError error={a.error} />
          <div className="fila" style={{ flexWrap: "wrap" }}>
            <button className="btn pri" onClick={() => registrar(false)} disabled={a.ocupado || !firma || sinRepresentante}>Registrar autorización firmada</button>
            <button className="btn" onClick={() => { setImpresa(true); imprimir(<AutorizacionDatosParaFirmar paciente={paciente} menor={menor}
              representante={paciente.representante?.nombre ? { nombre: paciente.representante.nombre, doc: paciente.representante.doc ?? "" } : null} />); }}
              title="Hoja con las mismas decisiones para que el paciente la firme a mano">Imprimir para firmar a mano</button>
            {impresa && !sinRepresentante && (
              <button className="btn" onClick={() => { if (confirm("¿Ya firmó la hoja en papel? Las decisiones marcadas aquí deben ser las que marcó en el papel. Conserva la hoja firmada en la historia clínica.")) registrar(true); }} disabled={a.ocupado}>Ya la firmó en papel: registrar</button>
            )}
          </div>
          <small className="muted">Versión {t.version} · Los datos clínicos se conservan por obligación legal aunque se revoque. Las decisiones se pueden cambiar después en Ficha, Datos personales ({FINES_DATOS.length} finalidades).</small>
        </section>
      </div>
    </>
  );
}
